import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Icon } from "@qgiv/core-react";
import { Divider } from "@qgiv/core-donor";
import { constants } from "@qgiv/core-js";
import { useFormikContext } from "formik";
import { getShouldSetRemainingFormToShow } from "@qgiv/p2p-registration";
import EarlyRegistrationChip from "./EarlyRegistrationChip";
import ConnectedCategoriesHeading from "./ConnectedCategoriesHeading";
import CommitmentDetailsChip from "./CommitmentDetailsChip";
import { setAppShowRemainingForm } from "../../../redux/slices/appSettingsSlice";

const SimpleCategory = ({
    details = "",
    controlId,
    earlyRegistration = false,
    formattedPrice = "",
    formattedDiscountedPrice = "",
    hasDiscount = false,
    id = "",
    isFree = false,
    isRegRoleIndividualOnly = false,
    isRegRoleTeamOnly = false,
    isVisible,
    enableTeams,
    hasCommitments,
    setAppShowRegistrationRole = () => {},
    setCurrentRegistrationCategory = () => {},
}) => {
    const {
        ENUMS: { EntityType },
    } = constants;
    const dispatch = useDispatch();
    const { setFieldTouched, setFieldValue, values } = useFormikContext();

    // currentRegistrationCategory is used for Early Registration
    // for $0 registrations. Need to set currently selected category in redux.
    // since there will only ever be one category in simple category forms,
    // there is no need to track for a change of category in state[registration][registrations]
    useEffect(() => {
        const Category = [id];

        // If Category and Full Registration Roles are present in new initial value then
        // we should show the remaining form, otherwise hide it to await selections.
        const shouldSetRemainingFormToShow =
            getShouldSetRemainingFormToShow(values);

        dispatch(setAppShowRemainingForm(shouldSetRemainingFormToShow));

        if (isRegRoleIndividualOnly) {
            setFieldTouched("Registration_Role");
            setFieldValue("Registration_Role", [`${EntityType.REGISTRATION}`]);
            dispatch(setAppShowRemainingForm(true));
        }
        if (isRegRoleTeamOnly) {
            setFieldTouched("Registration_Role");
            setFieldValue("Registration_Role", [`${EntityType.TEAM}`]);
        }

        dispatch(setCurrentRegistrationCategory(Category));
        dispatch(setAppShowRegistrationRole(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let formattedPriceUi = formattedPrice;
    let earlyRegistrationUi;
    let categoriesHeadingUi;
    let commitmentDetailsUi;
    if (hasDiscount) {
        formattedPriceUi = (
            <>
                <span className="-text-decoration--line-through -color--system-medium-light">
                    {formattedPrice}
                </span>{" "}
                {formattedDiscountedPrice}
            </>
        );
    }
    if (!isFree && earlyRegistration) {
        earlyRegistrationUi = (
            <div className="col col--12">
                <EarlyRegistrationChip categoryId={id} />
            </div>
        );
    }

    if (hasCommitments) {
        commitmentDetailsUi = (
            <div className="col col--12 -padding-top--10">
                <CommitmentDetailsChip
                    categoryId={id}
                    location="category"
                    details={details}
                />
            </div>
        );
    }
    // Do not need to ask users how they want to participate if there is no
    // team or category choice to make
    if (enableTeams) {
        // Add padding so that there is space below the divider line that sits
        // above the heading if the registration fee is visible
        categoriesHeadingUi = (
            <div className="grid">
                <div
                    className={`col col--12 ${
                        isVisible ? "" : "-padding-top--0"
                    }`}
                >
                    <ConnectedCategoriesHeading controlId={controlId} />
                </div>
            </div>
        );
    }

    return (
        <>
            {isVisible && (
                <>
                    <div className="grid">
                        <div className="col col--12 -padding-top--0">
                            <h3>
                                <Icon type="Custom" glyph="qgiv-tag-solid" />{" "}
                                Registration Fee: {formattedPriceUi}
                            </h3>
                        </div>
                        {earlyRegistrationUi}
                        {commitmentDetailsUi}
                    </div>
                    <Divider dashed spaceAboveOnly />
                </>
            )}
            {categoriesHeadingUi}
        </>
    );
};

SimpleCategory.propTypes = {
    controlId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    earlyRegistration: PropTypes.bool,
    hasCommitments: PropTypes.bool.isRequired,
    formattedPrice: PropTypes.string,
    formattedDiscountedPrice: PropTypes.string,
    hasDiscount: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isFree: PropTypes.bool,
    isRegRoleIndividualOnly: PropTypes.bool,
    isRegRoleTeamOnly: PropTypes.bool,
    isVisible: PropTypes.bool.isRequired,
    enableTeams: PropTypes.bool.isRequired,
    setAppShowRegistrationRole: PropTypes.func,
    setCurrentRegistrationCategory: PropTypes.func,
    details: PropTypes.string,
};

export default SimpleCategory;
